import localStorageService from '../utils/services/localStorage/localStorage';
import globalLogger from '../logger/logger';
import { TIMES } from '../../common/utils/timeUtil';

const logger = globalLogger.createChannel('appReviewService');

// Single localStorage key for all app review data
export const APP_REVIEW_DATA_KEY = 'milanote.appReview.data';

type AppReviewData = {
    lastSessionDate: number;
    sessionCount: number;
    hasPrompted: boolean;
};

/**
 * Gets the app review data from localStorage
 */
const getAppReviewData = (): AppReviewData => {
    const dataStr = localStorageService.getItem(APP_REVIEW_DATA_KEY);
    if (!dataStr) {
        return {
            lastSessionDate: 0,
            sessionCount: 0,
            hasPrompted: false,
        };
    }

    try {
        return JSON.parse(dataStr) as AppReviewData;
    } catch (error) {
        // Sensitive data audit: SAFE — Parse error for app review settings, no user data
        logger.warn('Failed to parse app review data, resetting to defaults', error);
        return {
            lastSessionDate: 0,
            sessionCount: 0,
            hasPrompted: false,
        };
    }
};

/**
 * Saves the app review data to localStorage
 */
const saveAppReviewData = (data: AppReviewData): void => {
    try {
        localStorageService.setItem(APP_REVIEW_DATA_KEY, JSON.stringify(data));
    } catch (error) {
        // Sensitive data audit: SAFE — Save error for app review settings, no user data
        logger.error('Failed to save app review data', error);
    }
};

/**
 * Checks if the current session is a new session (24+ hours since last session)
 * Updates the session count if it's a new session
 * @returns {boolean} true if this is a new session, false otherwise
 */
export const checkAndUpdateSessionCount = (): boolean => {
    const currentTimestampMs = new Date().getTime();
    const data = getAppReviewData();

    // If it's been more than 24 hours since the last session, count it as a new session
    if (currentTimestampMs - data.lastSessionDate > TIMES.DAY) {
        const newData = {
            ...data,
            lastSessionDate: currentTimestampMs,
            sessionCount: data.sessionCount + 1,
        };

        saveAppReviewData(newData);

        // Sensitive data audit: SAFE — Session count is aggregate metric, not personally identifiable
        logger.info('New app session detected', {
            sessionCount: newData.sessionCount,
        });
        return true;
    }

    // Update the last session date even if it's not a new session
    const updatedData = {
        ...data,
        lastSessionDate: currentTimestampMs,
    };
    saveAppReviewData(updatedData);

    return false;
};

/**
 * Gets the current session count
 * @returns {number} the number of sessions
 */
export const getSessionCount = (): number => {
    const data = getAppReviewData();
    return data.sessionCount;
};

/**
 * Checks if the app has already prompted the user for a review
 * @returns {boolean} true if the app has prompted for a review
 */
export const hasPromptedForReview = (): boolean => {
    const data = getAppReviewData();
    return data.hasPrompted;
};

/**
 * Marks that the app has prompted for a review
 */
export const markPromptedForReview = (): void => {
    const data = getAppReviewData();
    const updatedData = {
        ...data,
        hasPrompted: true,
    };

    saveAppReviewData(updatedData);
    // Sensitive data audit: SAFE — Static message about app review prompt, no user data
    logger.info('App review prompt shown to user');
};

/**
 * Determines if the app should prompt for a review based on:
 * 1. User has had at least 2 sessions with the app, 2 session -> 1 day apart
 * 2. User has created at least 10 elements if free user
 * 3. If user is subscribed we dont fetch element count so ignore element count
 * 3. App hasn't prompted for a review before on this device
 * @param elementCount number of elements the user has created
 * @returns {boolean} true if the app should prompt for a review
 */
export const shouldPromptForReview = (elementCount: number, isSubscribedUser: boolean): boolean => {
    const data = getAppReviewData();

    // Check if session count and element count criteria are met
    const hasMinimumSessions = data.sessionCount >= 2;
    const hasMinimumElements = elementCount > 10;

    if (!hasMinimumSessions || (!isSubscribedUser && !hasMinimumElements)) {
        // Sensitive data audit: SAFE — Session and element count are aggregate metrics, not personally identifiable
        logger.debug('Review criteria not met', {
            sessionCount: data.sessionCount,
            elementCount,
        });
        return false;
    }

    // Only show prompt if we've never prompted before
    if (!data.hasPrompted) {
        // Sensitive data audit: SAFE — Session and element count are aggregate metrics, not personally identifiable
        logger.info('Review criteria met - will show prompt', {
            sessionCount: data.sessionCount,
            elementCount,
        });
        return true;
    }

    // Already prompted before, don't show again
    // Sensitive data audit: SAFE — Static message about review prompt status, no user data
    logger.debug('Already prompted before, not showing review prompt again');
    return false;
};
